// @ts-nocheck
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { topicToHideFieldsArray } from "@store/Overrides/Widgets/ContactUs/ContactForm";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    companyName: state.components.contactUsForm.fieldValues["userCompany"],
    session: getSession(state),
    needRefresh: state.greif.pages.contactUs.resetValues,
    topic: state.components.contactUsForm.fieldValues["topic"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CompanyNameStyles {
    companyNameTextField?: TextFieldPresentationProps;
}

export const companyNameStyles: CompanyNameStyles = {};

const CompanyName: React.FC<Props> = ({ fields, companyName, setFieldValue, session, needRefresh, topic }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [companyNameErrorMessage, setCompanyNameErrorMessage] = React.useState("");
    const [hasValidated, setHasValidated] = React.useState(false);

    React.useEffect(() => {
        if (session.userCompany) {
            setFieldValue({ key: "userCompany", value: session.userCompany });
        }
    }, [needRefresh]);

    const styles = React.useMemo(() => {
        const showField = !topicToHideFieldsArray.includes(topic || "");
        return showField
            ? companyNameStyles
            : mergeProps<CompanyNameStyles>(companyNameStyles, {
                  companyNameTextField: {
                      cssOverrides: {
                          formField: css`
                              display: none;
                          `,
                      },
                  },
              });
    }, [topic]);

    React.useEffect(() => {
        contactFormContext.validators.companyName = validateCompanyName;
    }, [companyName]);

    React.useEffect(() => {
        if (hasValidated) {
            validateCompanyName();
        }
    }, [companyName, hasValidated]);

    React.useEffect(() => {
        contactFormContext.validators.companyName = validateCompanyName;
    }, [isRequired]);

    const validateCompanyName = (value?: string) => {
        setHasValidated(true);
        const effectiveValue = value ?? companyName ?? "";
        const errorMessage = isRequired && !effectiveValue ? (siteMessage("Field_Required", label) as string) : "";
        setCompanyNameErrorMessage(errorMessage);
        return !errorMessage;
    };

    const companyNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "userCompany", value: event.target.value });
        if (hasValidated) {
            validateCompanyName(event.target.value);
        }
    };

    return (
        <TextField
            {...styles.companyNameTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={companyName || ""}
            error={companyNameErrorMessage}
            onChange={companyNameChangeHandler}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CompanyName),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Company Name",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter Company Name",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
